import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supportApi, type TicketPriority } from '../lib/api'
import { apiError } from '../lib/errors'

const C = {
  bg: 'var(--color-bg)', card: 'var(--color-bg-card)', card2: 'var(--color-bg)',
  text: 'var(--color-text)', muted: 'var(--color-text-muted)', faint: 'var(--color-text-faint)',
  border: 'var(--color-border)', primary: 'var(--color-primary)',
}
const STATUS: Record<string, { label: string; color: string; bg: string }> = {
  open:                { label: 'Open',         color: '#F5A623', bg: 'rgba(245,166,35,0.13)' },
  in_progress:         { label: 'In progress',  color: '#3B82F6', bg: 'rgba(59,130,246,0.13)' },
  waiting_on_customer: { label: 'Needs your reply', color: '#A06CD5', bg: 'rgba(160,108,213,0.13)' },
  resolved:            { label: 'Resolved',     color: '#2D9B5A', bg: 'rgba(45,155,90,0.13)' },
  closed:              { label: 'Closed',       color: '#8A8F87', bg: 'rgba(138,143,135,0.13)' },
}
const CATEGORIES = ['general', 'billing', 'bug', 'feature_request', 'irrigation', 'ai']
const PRIORITIES: TicketPriority[] = ['low', 'medium', 'high', 'urgent']
const catLabel = (c: string) => c.replace(/_/g, ' ').replace(/\b\w/g, m => m.toUpperCase())
const when = (s: string) => new Date(s).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })

export function SupportPage() {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['support-tickets'], queryFn: () => supportApi.list().then(r => r.data.tickets) })
  const [openId, setOpenId] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  return (
    <main id="main-content" style={{ padding: 24, maxWidth: 1000, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, fontFamily: '"Syne", system-ui', color: C.text, margin: 0 }}>Support</h1>
        <button onClick={() => setCreating(true)} style={btn(C.primary, '#1a1207')}>＋ New ticket</button>
      </div>
      <p style={{ color: C.muted, fontSize: 13, marginTop: 0, marginBottom: 20 }}>
        Ask the Grafty team for help — billing, a bug, a how-to, or a feature request. We reply in the thread.
      </p>

      {isLoading ? (
        <p style={{ color: C.faint }}>Loading…</p>
      ) : !data || data.length === 0 ? (
        <div style={{ ...cardStyle(), textAlign: 'center', padding: 40, color: C.muted }}>
          No tickets yet. Click <strong style={{ color: C.text }}>＋ New ticket</strong> to ask us anything.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {data.map(t => {
            const st = STATUS[t.status] ?? STATUS.open
            return (
              <button key={t.id} onClick={() => setOpenId(t.id)} style={{ ...cardStyle(), textAlign: 'left', cursor: 'pointer', display: 'flex', gap: 12, alignItems: 'center' }}>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    {t.kind === 'feedback' && <span style={pill('#C77DFF', 'color-mix(in srgb, #C77DFF 16%, transparent)')}>Feedback</span>}
                    <span style={{ color: C.text, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.subject}</span>
                  </span>
                  <span style={{ display: 'block', color: C.faint, fontSize: 12, marginTop: 2 }}>
                    {catLabel(t.category)}{t.rating ? ` · ${'★'.repeat(t.rating)}` : ''} · updated {when(t.updated_at)}{t.reply_count > 0 ? ` · ${t.reply_count} ${t.reply_count === 1 ? 'reply' : 'replies'}` : ''}
                  </span>
                </span>
                <span style={pill(st.color, st.bg)}>{st.label}</span>
              </button>
            )
          })}
        </div>
      )}

      {creating && <CreateModal onClose={() => setCreating(false)} onCreated={(id) => { qc.invalidateQueries({ queryKey: ['support-tickets'] }); setCreating(false); setOpenId(id) }} />}
      {openId && <TicketModal id={openId} onClose={() => setOpenId(null)} />}
    </main>
  )
}

function CreateModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [category, setCategory] = useState('general')
  const [priority, setPriority] = useState<TicketPriority>('medium')
  const [err, setErr] = useState('')
  const m = useMutation({
    mutationFn: () => supportApi.create({ subject, body, category, priority }).then(r => r.data),
    onSuccess: (t) => onCreated(t.id),
    onError: (e) => setErr(apiError(e, 'Could not create the ticket')),
  })
  return (
    <Overlay onClose={onClose}>
      <h2 style={modalTitle()}>New support ticket</h2>
      <Label>Subject</Label>
      <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Short summary" style={inp()} />
      <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
        <span style={{ flex: 1 }}>
          <Label>Category</Label>
          <select value={category} onChange={e => setCategory(e.target.value)} style={inp()}>
            {CATEGORIES.map(c => <option key={c} value={c}>{catLabel(c)}</option>)}
          </select>
        </span>
        <span style={{ flex: 1 }}>
          <Label>Priority</Label>
          <select value={priority} onChange={e => setPriority(e.target.value as TicketPriority)} style={inp()}>
            {PRIORITIES.map(p => <option key={p} value={p}>{p[0].toUpperCase() + p.slice(1)}</option>)}
          </select>
        </span>
      </div>
      <div style={{ marginTop: 10 }}>
        <Label>How can we help?</Label>
        <textarea value={body} onChange={e => setBody(e.target.value)} rows={6} placeholder="Describe the issue or question…" style={{ ...inp(), resize: 'vertical' }} />
      </div>
      {err && <p style={errStyle()}>{err}</p>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
        <button onClick={onClose} style={btn('transparent', C.muted, true)}>Cancel</button>
        <button disabled={!subject.trim() || !body.trim() || m.isPending} onClick={() => { setErr(''); m.mutate() }} style={btn(C.primary, '#1a1207')}>
          {m.isPending ? 'Sending…' : 'Submit ticket'}
        </button>
      </div>
    </Overlay>
  )
}

function TicketModal({ id, onClose }: { id: string; onClose: () => void }) {
  const qc = useQueryClient()
  const { data: t, isLoading } = useQuery({ queryKey: ['support-ticket', id], queryFn: () => supportApi.get(id).then(r => r.data) })
  const [reply, setReply] = useState('')
  const refresh = () => { qc.invalidateQueries({ queryKey: ['support-ticket', id] }); qc.invalidateQueries({ queryKey: ['support-tickets'] }) }
  const replyM = useMutation({ mutationFn: () => supportApi.reply(id, reply), onSuccess: () => { setReply(''); refresh() } })
  const closeM = useMutation({ mutationFn: () => supportApi.close(id), onSuccess: refresh })
  const st = t ? (STATUS[t.status] ?? STATUS.open) : STATUS.open
  const closed = t?.status === 'closed'

  return (
    <Overlay onClose={onClose} wide>
      {isLoading || !t ? <p style={{ color: C.faint }}>Loading…</p> : (
        <>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <h2 style={{ ...modalTitle(), margin: 0, flex: 1 }}>{t.subject}</h2>
            <span style={pill(st.color, st.bg)}>{st.label}</span>
          </div>
          <p style={{ color: C.faint, fontSize: 12, margin: '4px 0 14px' }}>{catLabel(t.category)} · {t.priority} priority · opened {when(t.created_at)}</p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: '46vh', overflowY: 'auto', paddingRight: 4 }}>
            <Bubble who="You" staff={false} body={t.body} at={t.created_at} />
            {(t.replies ?? []).map(r => <Bubble key={r.id} who={r.author_name} staff={r.is_staff} body={r.body} at={r.created_at} />)}
          </div>

          {closed ? (
            <p style={{ color: C.faint, fontSize: 13, marginTop: 14 }}>This ticket is closed. Start a new ticket if you need more help.</p>
          ) : (
            <div style={{ marginTop: 14 }}>
              <textarea value={reply} onChange={e => setReply(e.target.value)} rows={3} placeholder="Write a reply…" style={{ ...inp(), resize: 'vertical' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10 }}>
                <button onClick={() => closeM.mutate()} disabled={closeM.isPending} style={btn('transparent', C.muted, true)}>Close ticket</button>
                <button disabled={!reply.trim() || replyM.isPending} onClick={() => replyM.mutate()} style={btn(C.primary, '#1a1207')}>{replyM.isPending ? 'Sending…' : 'Reply'}</button>
              </div>
            </div>
          )}
        </>
      )}
    </Overlay>
  )
}

function Bubble({ who, staff, body, at }: { who: string; staff: boolean; body: string; at: string }) {
  return (
    <div style={{ background: staff ? 'rgba(45,155,90,0.10)' : C.card2, border: `1px solid ${staff ? 'rgba(45,155,90,0.35)' : C.border}`, borderRadius: 10, padding: '10px 12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: staff ? '#2D9B5A' : C.text }}>{staff ? `${who} · Grafty` : who}</span>
        <span style={{ fontSize: 11, color: C.faint }}>{when(at)}</span>
      </div>
      <div style={{ color: C.text, fontSize: 14, whiteSpace: 'pre-wrap' }}>{body}</div>
    </div>
  )
}

function Overlay({ children, onClose, wide }: { children: React.ReactNode; onClose: () => void; wide?: boolean }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 24, width: '100%', maxWidth: wide ? 620 : 480, maxHeight: '90vh', overflowY: 'auto' }}>
        {children}
      </div>
    </div>
  )
}

const cardStyle = (): React.CSSProperties => ({ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '12px 16px', width: '100%' })
const modalTitle = (): React.CSSProperties => ({ fontSize: 18, fontWeight: 700, fontFamily: '"Syne", system-ui', color: C.text, margin: '0 0 14px' })
const inp = (): React.CSSProperties => ({ width: '100%', background: C.card2, border: `1px solid ${C.border}`, borderRadius: 8, padding: '9px 11px', color: C.text, fontSize: 14 })
const errStyle = (): React.CSSProperties => ({ color: '#E5484D', fontSize: 12, marginTop: 10 })
const pill = (color: string, bg: string): React.CSSProperties => ({ background: bg, color, fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 999, whiteSpace: 'nowrap' })
function btn(bg: string, color: string, ghost = false): React.CSSProperties {
  return { background: ghost ? 'transparent' : bg, color, border: ghost ? `1px solid ${C.border}` : 'none', borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }
}
function Label({ children }: { children: React.ReactNode }) {
  return <label style={{ display: 'block', fontSize: 12, color: C.muted, marginBottom: 5 }}>{children}</label>
}
